import { DropdownMenuItem } from '@/components/ui/dropdown-menu';
import Text from '@/components/ui/text';
import { useToast } from '@/components/ui/use-toast';
import { Can } from '@/features/acl/can';
import type { ConfirmationDialogProps } from '@/features/dialog/confirmation-dialog';
import { ConfirmationDialog } from '@/features/dialog/confirmation-dialog';
import { DialogStateManager } from '@/features/dialog/dialog-provider';
import { CreateNewDraftLink } from '@/features/sales-draft/components/create-new-draft-link';
import { useLocale } from '@/lib/hooks/use-locale';
import { trpcClient } from '@/utils/trpc-client';
import type { SrSalesOrderType } from '@plato/data/starrocks/types';
import { Rocket, Trash2, Trash2Icon } from 'lucide-react';
import { Trans } from 'react-i18next';
import { useDetailQuoteOrder } from '../hooks/use-detail-quote-order';
import { QuoteOrderReleaseDialog } from './quote-order-release-dialog';

export interface QuoteOrderDropdownMenuItemProps {
    transactionId: string;
    type: SrSalesOrderType;
    onDelete?: () => void;
}

export const DropdownMenuItemDelete = ({
    transactionId,
    type,
    onDelete,
}: QuoteOrderDropdownMenuItemProps) => {
    const { t } = useLocale('quotes-orders-actions');
    const { toast } = useToast();

    const handleDelete = () => {
        DialogStateManager.show<any, ConfirmationDialogProps, ConfirmationDialogProps>(ConfirmationDialog, {
            id: 'delete-confirmation-dialog',
            focus: 'destroy',
            icon: Trash2Icon,
            title: t(`${type}__delete_confirmation__title`),
            description: t(`${type}__delete_confirmation__description`),
            accept: {
                text: t('form__delete', { ns: 'common' }),
                action: async () => {
                    try {
                        await trpcClient.platoUser.command.salesOrder.order.delete.mutate({
                            sales_order_id: transactionId,
                        });

                        toast({
                            icon: 'success',
                            title: t(`${type}__delete_toast__success_title`),
                            description: (
                                <Trans
                                    t={t}
                                    i18nKey={`${type}__delete_toast__success_description`}
                                    components={{
                                        linkTag: <CreateNewDraftLink />,
                                    }}
                                />
                            ),
                        });

                        onDelete?.();
                    } catch (error) {
                        toast({
                            icon: 'warning',
                            title: 'Error',
                        });
                    }
                },
            },
            reject: {
                text: t('form__cancel', { ns: 'common' }),
            },
        });
    };

    return (
        <Can I="delete" a="QuoteOrder">
            <DropdownMenuItem onSelect={handleDelete}>
                <Trash2 className="h-4 w-4" />
                <Text>{t('dropdown_option__delete', { ns: 'quotes-orders-actions' })}</Text>
            </DropdownMenuItem>
        </Can>
    );
};

export const DropdownMenuItemRelease = ({ transactionId, type }: QuoteOrderDropdownMenuItemProps) => {
    const { t } = useLocale('quotes-orders-actions');

    const { data, isLoading } = useDetailQuoteOrder({ transactionId });

    if (isLoading || data?.header.released_date) {
        return null;
    }

    const handleSelect = () => {
        DialogStateManager.show(QuoteOrderReleaseDialog, {
            transactionId,
            type,
        });
    };

    return (
        <Can I="release" a="QuoteOrder">
            <DropdownMenuItem onSelect={handleSelect}>
                <Rocket className="h-4 w-4" />
                <Text>{t('dropdown_option_release', { ns: 'quotes-orders-actions' })}</Text>
            </DropdownMenuItem>
        </Can>
    );
};
